import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

type Props = Readonly<{ code: string | null }>;

/**
 * Fallback shown when a component fails to render. Keeps the familiar error
 * icon but, when a correlation code is available, shows it with a copy button
 * so the user can send it to support (it maps to a backend log entry).
 */
function ComponentErrorFallback({ code }: Props) {
    // useSuspense:false so this fallback never suspends while translations load.
    const { t } = useTranslation('frontend', { useSuspense: false });
    const [copied, setCopied] = useState(false);

    const copy = () => {
        if (!code) {
            return;
        }
        navigator.clipboard
            ?.writeText(code)
            .then(() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            })
            .catch(() => {
                /* clipboard unavailable — the code is still visible to copy manually */
            });
    };

    return (
        <div className="text-center text-muted py-4">
            <div className="display-1">
                <i className="far fa-times-circle" />
            </div>
            <p className="mb-2">
                {t('errorReporting.failedToLoad', {
                    defaultValue: 'Something went wrong while loading this section. Please refresh the page.',
                })}
            </p>
            {code && (
                <div className="small">
                    <span className="me-2">
                        {t('errorReporting.errorCode', { defaultValue: 'Error code' })}: <code>{code}</code>
                    </span>
                    <button type="button" className="btn btn-sm btn-outline-secondary" onClick={copy}>
                        {copied
                            ? t('errorReporting.copied', { defaultValue: 'Copied' })
                            : t('errorReporting.copy', { defaultValue: 'Copy' })}
                    </button>
                </div>
            )}
        </div>
    );
}

export default ComponentErrorFallback;
